import {
  BasePagination,
  Button,
  Flex,
  HStack,
  Icon,
  Spacer,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from '@elkaso-app/web-design';
import { useDownloadUnmatchedDemandCsvApi, useGetUnmatchedMasterProductDemandApi } from 'apis/restaurant-inquiries';
import { BaseTableContainer } from 'components/base-table';
import { BaseTableSpinner, BaseTableTitle, EmptyTableState } from 'components/base-table/components';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import React, { useEffect, useState } from 'react';
import { BiDownload } from 'react-icons/bi';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { CategoryFilterButton } from '../components/category-filter-button';
import { DemandTableRow } from '../components/demand-table-row';
import { OriginFilterButton } from '../components/origin-filter-button';
import { SortMenu } from '../components/sort-menu';
import { StatusTabs } from '../components/status-tabs';

export const UnmatchedDemandListScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();
  const dispatch = useAppDispatch();
  const { isLoading, isFetching, data } = useGetUnmatchedMasterProductDemandApi();
  const { refetch: downloadCsv, isFetching: isDownloading } = useDownloadUnmatchedDemandCsvApi();
  const { pageCount, total, data: rows, summary } = data ?? {};

  const [expandedIds, setExpandedIds] = useState<Record<number, boolean>>({});

  useEffect(() => {
    dispatch(changeTitle('Unmatched Master Product Demand'));
  }, []);

  const toggleRow = (masterProductId: number) =>
    setExpandedIds((prev) => ({ ...prev, [masterProductId]: !prev[masterProductId] }));

  const originOptions = React.useMemo(
    () =>
      Array.from(new Set((rows ?? []).map((row) => row.origin).filter((origin): origin is string => !!origin))).sort(),
    [rows]
  );

  return (
    <>
      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <VStack align='start' spacing='2px'>
              <Flex align='center' gap='sm'>
                <BaseTableTitle title='Unmatched Master Product Demand' />
                <BaseTableSpinner isFetching={isFetching} />
              </Flex>
              {summary && (
                <Text fontSize='sm' color='gray.500'>
                  {summary.totalProducts} products ·{' '}
                  <Text as='span' color='red.500' fontWeight='bold'>
                    {summary.totalOpenInquiries} open inquiries
                  </Text>{' '}
                  with no confirmed supplier match
                </Text>
              )}
            </VStack>
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <HStack spacing='sm'>
              <Button
                size='sm'
                variant='outline'
                leftIcon={<Icon as={BiDownload} />}
                isLoading={isDownloading}
                onClick={() => downloadCsv()}>
                Export
              </Button>
            </HStack>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <Flex px='md' pb='md' gap='sm' align='center' wrap='wrap' borderBottom='1px solid' borderColor='gray.200'>
          <StatusTabs summary={summary} />
          <CategoryFilterButton />
          <OriginFilterButton options={originOptions} />
          <Spacer />
          <SortMenu />
        </Flex>

        <BaseTableContainer.TableWrapper>
          <Table size='sm'>
            <Thead position='sticky' top={0} bg='white' zIndex={1}>
              <Tr>
                <Th w='24%'>Product</Th>
                <Th w='16%'>Demand</Th>
                <Th w='25%'>Match status</Th>
                <Th w='13%'>Waiting</Th>
                <Th w='22%' textAlign='right'>
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows?.length ? (
                rows.map((row) => (
                  <DemandTableRow
                    key={row.masterProductId}
                    row={row}
                    isExpanded={!!expandedIds[row.masterProductId]}
                    onToggle={() => toggleRow(row.masterProductId)}
                  />
                ))
              ) : (
                <EmptyTableState />
              )}
            </Tbody>
          </Table>
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount ?? 0} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total ?? 0} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>
    </>
  );
};
